import { EnrichedCategoryReport, PaasTransaction } from 'vopay';
import type { EnrichedReports } from '#shared/hooks/useApi/useFetchEnrichedReports';
import { CategoryDescriptor, pickRampColors } from './categories';

export type DateRangeDays = 90 | 60 | 30;

export type CategoryTotal = {
  descriptor: CategoryDescriptor;
  amount: number;
  color: string;
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Aggregator timestamps are mostly midnight placeholders, so tables show the
// date part only
export function formatTransactionDate(transactionDate: string): string {
  return transactionDate.slice(0, 10);
}

// Month keys are 'YYYY-MM' as returned in SumPerMonth / CountPerMonth
export function formatMonthKey(monthKey: string, { withYear = false }: { withYear?: boolean } = {}): string {
  const [year, month] = monthKey.split('-');
  const name = MONTH_NAMES[Number(month) - 1] ?? monthKey;

  return withYear ? `${name} ${year}` : name;
}

// The visible window is the last N calendar months present in the enriched
// data (the reports are all-time; recency is anchored on the data itself).
export function getVisibleMonths(reports: EnrichedReports, rangeDays: DateRangeDays): string[] {
  const allMonths = new Set<string>();

  Object.values(reports.categories).forEach((report) => {
    Object.keys(report?.SumPerMonth ?? {}).forEach((monthKey) => allMonths.add(monthKey));
  });

  const sorted = [...allMonths].sort();
  const monthCount = Math.max(1, Math.round(rangeDays / 30));

  return sorted.slice(-monthCount);
}

function sumForMonths(report: EnrichedCategoryReport | null | undefined, months: string[]): number {
  if (!report) return 0;

  return months.reduce((sum, monthKey) => sum + (report.SumPerMonth[monthKey] ?? 0), 0);
}

function countForMonths(report: EnrichedCategoryReport | null | undefined, months: string[]): number {
  if (!report) return 0;

  return months.reduce((sum, monthKey) => sum + (report.CountPerMonth[monthKey] ?? 0), 0);
}

// Totals per category within the window, largest first, colored off the ramp
export function buildCategoryTotals(
  descriptors: CategoryDescriptor[],
  reports: EnrichedReports,
  months: string[],
): CategoryTotal[] {
  const totals = descriptors
    .map((descriptor) => ({ descriptor, amount: sumForMonths(reports.categories[descriptor.reportType], months) }))
    .filter(({ amount }) => amount > 0)
    .sort((a, b) => b.amount - a.amount);

  const colors = pickRampColors(totals.length);

  return totals.map((total, index) => ({ ...total, color: colors[index] }));
}

export function buildMonthlySeries(
  descriptors: CategoryDescriptor[],
  reports: EnrichedReports,
  months: string[],
): number[] {
  return months.map((monthKey) =>
    descriptors.reduce(
      (sum, descriptor) => sum + (reports.categories[descriptor.reportType]?.SumPerMonth[monthKey] ?? 0),
      0,
    ),
  );
}

export function countEnrichedTransactions(
  descriptors: CategoryDescriptor[],
  reports: EnrichedReports,
  months: string[],
): number {
  return descriptors.reduce(
    (sum, descriptor) => sum + countForMonths(reports.categories[descriptor.reportType], months),
    0,
  );
}

export function categoryTotalFor(descriptor: CategoryDescriptor, reports: EnrichedReports, months: string[]): number {
  return sumForMonths(reports.categories[descriptor.reportType], months);
}

// ── Raw transaction rows (drawer tables) ──

export function rowAmount(row: PaasTransaction, descriptor: CategoryDescriptor): number {
  const amount = descriptor.side === 'credit' ? row.Credit : row.Debit;

  return amount ?? 0;
}

export function filterRowsByCategory(
  rows: PaasTransaction[],
  descriptor: CategoryDescriptor,
  months: string[],
): PaasTransaction[] {
  return rows
    .filter((row) => {
      const sideMatches = descriptor.side === 'credit' ? row.Credit !== null : row.Debit !== null;

      return (
        sideMatches &&
        row.Classification === descriptor.classification &&
        months.includes(row.TransactionDate.slice(0, 7))
      );
    })
    .sort((a, b) => b.TransactionDate.localeCompare(a.TransactionDate));
}

// ── Enhanced-tier derived values ──

export type RiskLevel = 'low' | 'moderate' | 'high' | 'veryHigh';

export function riskLevel(score: number): RiskLevel {
  if (score < 25) return 'low';
  if (score < 50) return 'moderate';
  if (score < 75) return 'high';

  return 'veryHigh';
}

export type VariabilityRating = 'excellent' | 'good' | 'fair' | 'inconsistent';

// Thresholds follow the metric definition: under 10% excellent, 10–20% good,
// 21–30% fair, above 30% inconsistent
export function variabilityRating(coefficientPct: number): VariabilityRating {
  if (coefficientPct < 10) return 'excellent';
  if (coefficientPct <= 20) return 'good';
  if (coefficientPct <= 30) return 'fair';

  return 'inconsistent';
}

// The risk report's TransactionFrequency is transactions-per-day; the UI shows
// average days between transactions
export function daysBetweenTransactions(transactionsPerDay: number): number | null {
  if (!transactionsPerDay || transactionsPerDay <= 0) return null;

  return 1 / transactionsPerDay;
}
